// The Updates page — what changed, in the operator's language.
//
// WHY A CONSOLE NEEDS ONE. This product is changing weekly against a live
// client. A console that moves under someone without saying so is a console
// they stop trusting: the first time a panel is somewhere new and nothing
// explained it, every later change reads as a fault rather than as work. That
// is expensive in a tool whose entire value is being believed about the sea.
//
// WHAT IT IS NOT. Not a changelog. There are no version numbers, no commit
// hashes, no file names, no branch or ticket references, and no technical
// vocabulary — see the note in updates-data.ts about the bundle, not just the
// page. A party chief reading this wants to know whether the number they read
// yesterday means the same thing today.
//
// CORRECTIONS LEAD. When a figure an operator relies on was wrong and has been
// fixed, that is the most important thing on the page and it is ordered first
// within its release. The instinct is to bury it under the new features; the
// reason not to is that people trust a product that says plainly "this was
// wrong, here is what it reads now" and quietly stop trusting one where they
// find out on their own.

import { UPDATES, type UpdateKind, type UpdateItem } from "./updates-data.js";
import type { SiteNavOptions } from "./site.js";

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (className !== undefined) n.className = className;
  if (text !== undefined) n.textContent = text;
  return n;
}

/** The word an operator reads, not the word a developer would use. */
const KIND_LABEL: Record<UpdateKind, string> = {
  added: "New",
  changed: "Changed",
  fixed: "Corrected",
  data: "Data",
};

/**
 * Ordering within a release: corrections first, then data, then new, then
 * changed. A correction to a number someone acted on outranks a new feature,
 * however much more interesting the feature is to whoever built it.
 */
const KIND_ORDER: Record<UpdateKind, number> = { fixed: 0, data: 1, added: 2, changed: 3 };

function fmtDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric", month: "long", day: "numeric", timeZone: "UTC",
  });
}

function itemEl(it: UpdateItem): HTMLElement {
  const row = el("li", `upd-item upd-${it.kind}`);
  row.append(
    el("span", `upd-kind upd-kind-${it.kind}`, KIND_LABEL[it.kind]),
    el("span", "upd-title", it.title),
  );
  if (it.body !== undefined && it.body !== "") {
    row.appendChild(el("p", "upd-body", it.body));
  }
  return row;
}

export function mountUpdates(container: HTMLElement, _opts: SiteNavOptions): void {
  document.title = "Updates — SeaState";

  const doc = el("div", "doc");
  doc.appendChild(el("h1", undefined, "Updates"));
  doc.appendChild(
    el(
      "div",
      "doc-lede",
      "What has changed in the console, newest first. Corrections to figures are " +
        "listed before anything else — if a number you rely on has changed meaning, " +
        "it is at the top of its date.",
    ),
  );

  if (UPDATES.length === 0) {
    // An empty page states its own emptiness. A blank panel reads as broken.
    doc.appendChild(
      el("p", "note", "No updates have been published yet."),
    );
    container.appendChild(doc);
    return;
  }

  let lastDate = "";
  for (const rel of UPDATES) {
    const sec = el("section", "upd-release");
    // Two deploys can land on one day, and they are genuinely two releases — the
    // dates are not merged in the data, because that would misdate whichever one
    // was already live. What is suppressed is the repeated HEADING: the same date
    // printed twice down the page reads as a fault in the page rather than as two
    // releases, which is the opposite of what this page is for.
    if (rel.date !== lastDate) {
      sec.appendChild(el("h2", "upd-date", fmtDate(rel.date)));
      lastDate = rel.date;
    }

    const list = el("ul", "upd-list");
    const sorted = [...rel.items].sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind]);
    for (const it of sorted) list.appendChild(itemEl(it));
    sec.appendChild(list);

    // Said, not hidden. A release is rarely only the part that shows, and a
    // reader who is told "and eleven other changes you will not notice" trusts
    // the list more than one who is quietly shown a curated four.
    if (rel.internalCount > 0) {
      sec.appendChild(
        el(
          "p",
          "upd-internal",
          `Also ${rel.internalCount} change${rel.internalCount === 1 ? "" : "s"} with no ` +
            `effect you would see — groundwork, checks and speed.`,
        ),
      );
    }
    doc.appendChild(sec);
  }

  container.appendChild(doc);
}
